/**
 * /mail/operator/sends - operator-internal mail history (OCP-206).
 *
 * Lists orboto's own transactional sends (waitlist DOI, drip, password
 * reset, tenant welcome, etc.) — the OCP-side window into what went
 * out through OMS's /api/v1/internal/send endpoint. Daniel asked for
 * stats card + list + email-body detail-pane so he doesn't have to
 * SSH into the OMS admin UI for routine "did the welcome email send?"
 * checks.
 *
 * Data sources (all proxied through OCP admin routes):
 *   GET /v1/admin/mail/operator/stats  — 7-day status breakdown
 *   GET /v1/admin/mail/operator/sends  — paginated list
 *   GET /v1/admin/mail/operator/sends/:id  — detail incl. body
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface Stats {
  window_days: number;
  total: number;
  counts: Record<string, number>;
}

interface SendRow {
  id: string;
  from_address: string;
  to_address: string;
  subject: string | null;
  status: 'queued' | 'delivered' | 'bounced' | 'bounced-transient' | 'complained' | 'rejected';
  region: string | null;
  message_id: string | null;
  bounce_type: string | null;
  complaint_type: string | null;
  rejected_reason: string | null;
  size_bytes: number | null;
  created_at: string;
  delivered_at: string | null;
  bounced_at: string | null;
}

interface SendsListResponse {
  sends: SendRow[];
  nextCursor: string | null;
}

interface SendDetail extends SendRow {
  /** OCP-636 - Reply-To as OMS recorded it; absent until OMS persists the field on `oms_sends`. */
  reply_to?: string | null;
  body_html: string | null;
  body_text: string | null;
  tags: Record<string, string> | null;
}

const STATUS_FILTERS = [
  '',
  'queued',
  'delivered',
  'bounced',
  'bounced-transient',
  'complained',
  'rejected',
] as const;

export function AdminMailOperatorSendsPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [sends, setSends] = useState<SendRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SendDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // ---- stats card load ----
  useEffect(() => {
    (async () => {
      try {
        const r = await api<Stats>('/v1/admin/mail/operator/stats');
        setStats(r);
      } catch (err) {
        if (err instanceof ApiError) setStatsError(`Could not load stats (HTTP ${err.status}).`);
        else setStatsError('Could not load stats.');
      }
    })();
  }, []);

  // ---- list load ----
  const loadList = useCallback(
    async (mode: 'replace' | 'append') => {
      setListLoading(true);
      setListError(null);
      try {
        const params = new URLSearchParams();
        params.set('limit', '50');
        if (statusFilter) params.set('status', statusFilter);
        if (mode === 'append' && nextCursor) params.set('cursor', nextCursor);
        const r = await api<SendsListResponse>(
          `/v1/admin/mail/operator/sends?${params}`,
        );
        if (mode === 'append') setSends((prev) => [...prev, ...r.sends]);
        else {
          setSends(r.sends);
          setSelectedId(null);
          setDetail(null);
        }
        setNextCursor(r.nextCursor);
      } catch (err) {
        if (err instanceof ApiError) setListError(`Could not load sends (HTTP ${err.status}).`);
        else setListError('Could not load sends.');
      } finally {
        setListLoading(false);
      }
    },
    [statusFilter, nextCursor],
  );

  useEffect(() => {
    void loadList('replace');
  }, [statusFilter]);

  // ---- detail load on selection ----
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setDetailError(null);
      setDetail(null);
      try {
        const r = await api<SendDetail>(`/v1/admin/mail/operator/sends/${selectedId}`);
        if (!cancelled) setDetail(r);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setDetailError(`Could not load detail (HTTP ${err.status}).`);
        else setDetailError('Could not load detail.');
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Operator mail</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          OCP&apos;s transactional sends through orboto Mail (waitlist DOI, password resets,
          tenant welcome, drip sequence). Last 7 days of activity + the send log.
        </p>
      </div>

      {/* ---- Stats card ---- */}
      <section data-testid="operator-mail-stats">
        <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Last 7 days</h2>
        {statsError && (
          <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
            {statsError}
          </div>
        )}
        {!stats && !statsError && <p className="text-sm text-gray-500">Loading stats...</p>}
        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            <StatCard label="Total" value={stats.total} tone="neutral" />
            <StatCard label="Delivered" value={stats.counts.delivered ?? 0} tone="green" />
            <StatCard label="Queued" value={stats.counts.queued ?? 0} tone="sky" />
            <StatCard label="Bounced" value={stats.counts.bounced ?? 0} tone="red" />
            <StatCard label="Complained" value={stats.counts.complained ?? 0} tone="amber" />
            <StatCard label="Rejected" value={stats.counts.rejected ?? 0} tone="neutral" />
          </div>
        )}
      </section>

      {/* ---- List + detail split ---- */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_3fr]">
        {/* List pane */}
        <section data-testid="operator-mail-list">
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Sends ({sends.length}{nextCursor ? '+' : ''})</h2>
            <div className="flex items-center gap-2 text-xs">
              <label htmlFor="operator-mail-status" className="text-gray-500">Status:</label>
              <select
                id="operator-mail-status"
                data-testid="operator-mail-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs"
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s || 'all'} value={s}>
                    {s || 'All'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {listError && (
            <div role="alert" className="mb-2 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
              {listError}
            </div>
          )}
          {listLoading && sends.length === 0 && (
            <p className="text-sm text-gray-500">Loading...</p>
          )}
          {!listLoading && sends.length === 0 && !listError && (
            <p className="text-sm text-gray-500">
              No operator sends recorded yet for this status filter.
            </p>
          )}
          {sends.length > 0 && (
            <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2">Sent</th>
                    <th className="px-3 py-2">To</th>
                    <th className="px-3 py-2">Subject</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {sends.map((s) => (
                    <tr
                      key={s.id}
                      data-testid={`operator-mail-row-${s.id}`}
                      onClick={() => setSelectedId(s.id)}
                      className={`cursor-pointer ${selectedId === s.id ? 'bg-brand-50' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
                    >
                      <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">{formatTimestamp(s.created_at)}</td>
                      <td className="px-3 py-2 text-xs">{s.to_address}</td>
                      <td className="px-3 py-2 text-xs">{s.subject ?? '-'}</td>
                      <td className="px-3 py-2 text-xs">
                        <StatusBadge status={s.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {nextCursor && (
            <div className="mt-2 flex justify-center">
              <button
                type="button"
                data-testid="operator-mail-load-more"
                disabled={listLoading}
                onClick={() => void loadList('append')}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
              >
                {listLoading ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </section>

        {/* Detail pane */}
        <section data-testid="operator-mail-detail">
          {!selectedId && (
            <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
              Select a send on the left to inspect the email body, headers, and routing region.
            </div>
          )}
          {detailLoading && <p className="text-sm text-gray-500">Loading detail...</p>}
          {detailError && (
            <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
              {detailError}
            </div>
          )}
          {detail && <DetailView detail={detail} />}
        </section>
      </div>
    </div>
  );
}

function DetailView({ detail }: { detail: SendDetail }) {
  return (
    <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{detail.subject ?? '(no subject)'}</h2>
      <dl className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 text-xs">
        <KV label="From" value={detail.from_address} />
        {detail.reply_to && <KV label="Reply-To" value={detail.reply_to} />}
        <KV label="To" value={detail.to_address} />
        <KV label="Status" value={detail.status} />
        <KV label="Region" value={detail.region ?? '-'} />
        <KV label="Message id" value={detail.message_id ?? '-'} mono />
        <KV label="Created" value={formatTimestamp(detail.created_at)} />
        <KV label="Delivered" value={detail.delivered_at ? formatTimestamp(detail.delivered_at) : '-'} />
        <KV label="Bounced" value={detail.bounced_at ? formatTimestamp(detail.bounced_at) : '-'} />
        {detail.bounce_type && <KV label="Bounce type" value={detail.bounce_type} />}
        {detail.complaint_type && <KV label="Complaint type" value={detail.complaint_type} />}
        {detail.rejected_reason && <KV label="Rejected reason" value={detail.rejected_reason} />}
        {detail.size_bytes !== null && <KV label="Size" value={`${detail.size_bytes} B`} />}
      </dl>
      {detail.tags && Object.keys(detail.tags).length > 0 && (
        <div className="mt-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">Tags</h3>
          <pre className="mt-1 overflow-x-auto rounded bg-gray-50 dark:bg-gray-800 p-2 text-xs">
            {JSON.stringify(detail.tags, null, 2)}
          </pre>
        </div>
      )}
      {detail.body_html && (
        <div className="mt-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">HTML body</h3>
          <iframe
            data-testid="operator-mail-html-iframe"
            title="email html body"
            srcDoc={detail.body_html}
            sandbox=""
            className="mt-1 h-96 w-full rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900"
          />
        </div>
      )}
      {detail.body_text && (
        <div className="mt-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">Plain text</h3>
          <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-gray-50 dark:bg-gray-800 p-2 text-xs">
            {detail.body_text}
          </pre>
        </div>
      )}
    </div>
  );
}

function KV({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`mt-0.5 break-all text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone: 'green' | 'sky' | 'red' | 'amber' | 'neutral' }) {
  const toneCls =
    tone === 'green' ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900'
      : tone === 'sky' ? 'border-sky-200 bg-sky-50 text-sky-900'
      : tone === 'red' ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-900 dark:text-red-200'
      : tone === 'amber' ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200'
      : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100';
  return (
    <div className={`rounded border p-3 ${toneCls}`} data-testid={`operator-mail-stat-${label.toLowerCase()}`}>
      <div className="text-[10px] font-medium uppercase tracking-wide opacity-75">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: SendRow['status'] }) {
  const cls =
    status === 'delivered' ? 'bg-emerald-100 text-emerald-800 dark:text-emerald-300'
      : status === 'queued' ? 'bg-sky-100 text-sky-800'
      : status === 'bounced' ? 'bg-red-100 text-red-800 dark:text-red-300'
      : status === 'bounced-transient' ? 'bg-amber-100 text-amber-900 dark:text-amber-200'
      : status === 'complained' ? 'bg-amber-100 text-amber-900 dark:text-amber-200'
      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  return <span className={`rounded px-1.5 py-0.5 ${cls}`}>{status}</span>;
}

function formatTimestamp(iso: string): string {
  try {
    return fmtDateTime(iso);
  } catch {
    return iso;
  }
}
